import type { ReviewCycleDetail } from "#shared/reviewCycleSchemas.ts";
import { SectionTitle } from "@packages/core-client/components/SectionTitle.tsx";
import { PersonAvatar } from "@packages/core-client/people/PersonAvatar.tsx";
import { CycleExportMenu } from "./CycleExportMenu.tsx";
import { useI18nextInstance } from "@squide/i18next";
import type { ReactNode } from "react";
import { useLocale } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { reviewCyclesI18NextInstanceKey } from "../../../i18n/i18next.ts";
import { formatEvaluationPeriod, formatShortDate } from "../../reviewsDisplay.ts";
import { CycleStateDot } from "../list/CycleStateBadge.tsx";

interface ReviewCycleAttributeProps {
    label: string;
    children: ReactNode;
}

// intent-ui-deviation: hand-rolled dl/dt/dd instead of Intent UI's DescriptionList — it is a two-column grid
function ReviewCycleAttribute({ label, children }: ReviewCycleAttributeProps) {
    return (
        <div className="border-border/40 px-4 first:pl-0 sm:border-l sm:first:border-l-0">
            <dt className="text-muted-fg text-xs font-semibold tracking-widest uppercase">{label}</dt>
            <dd className="text-fg mt-1.5 text-sm tabular-nums">{children}</dd>
        </div>
    );
}

export interface CycleDetailHeaderProps {
    cycle: ReviewCycleDetail;
    actions?: ReactNode;
}

export function CycleDetailHeader({ cycle, actions }: CycleDetailHeaderProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("CycleDetailHeader", { i18n: i18nextInstance });
    const { t: tStates } = useTranslation("CycleStateBadge", { i18n: i18nextInstance });
    const { locale } = useLocale();

    const evaluationPeriod = formatEvaluationPeriod(cycle.evaluationStartDate, cycle.evaluationEndDate, locale);

    return (
        <header className="flex flex-col gap-6">
            <div className="flex items-start gap-4">
                <SectionTitle size="page" className="min-w-0 flex-1">
                    <span>{cycle.name}</span>
                </SectionTitle>

                <div className="shrink-0 [[data-loading]_&]:hidden">
                    <CycleExportMenu cycleId={cycle.id} />
                </div>

                {actions && <div className="shrink-0">{actions}</div>}
            </div>

            <dl className="flex flex-wrap gap-y-4">
                <ReviewCycleAttribute label={t("status")}>
                    <span className="flex items-center gap-2">
                        <CycleStateDot state={cycle.state} />
                        <span>{tStates(cycle.state)}</span>
                    </span>
                </ReviewCycleAttribute>

                {cycle.state === "scheduled" && cycle.automatedStartDate && <ReviewCycleAttribute label={t("automatedStartDate")}>{formatShortDate(cycle.automatedStartDate, locale)}</ReviewCycleAttribute>}

                {evaluationPeriod && <ReviewCycleAttribute label={t("evaluationPeriod")}>{evaluationPeriod}</ReviewCycleAttribute>}
                {cycle.launchDate && <ReviewCycleAttribute label={t("launchDate")}>{formatShortDate(cycle.launchDate, locale)}</ReviewCycleAttribute>}
                {cycle.lockedDate && <ReviewCycleAttribute label={t("lockedDate")}>{formatShortDate(cycle.lockedDate, locale)}</ReviewCycleAttribute>}

                <ReviewCycleAttribute label={t("creator")}>
                    {cycle.creator ? (
                        <span className="flex items-center gap-2">
                            <PersonAvatar person={cycle.creator} size="xs" />
                            <span className="min-w-0 truncate">{cycle.creator.displayName}</span>
                        </span>
                    ) : (
                        <span className="text-muted-fg">{t("unknownCreator")}</span>
                    )}
                </ReviewCycleAttribute>
            </dl>
        </header>
    );
}
